import React, {useState} from 'react';

import {Button, Icon, Menu, SplitButton, WithTooltip} from '@albert-io/atomic';
import {history} from 'client/history';

import {useAssignmentReleaseScope} from './hooks/useAssignmentReleaseScope';
import {NO_RELEASABLE_SUBMISSIONS} from './AssignmentsList.helpers';
import ReleaseGradesModal from './ReleaseGradesModal.react';

/** Why both segments are dead before anyone has answered — distinct from having nothing releasable. */
const NO_SUBMISSIONS_TO_GRADE = 'No submissions to grade yet';

interface AssignmentGradeSplitButtonProps {
  assignmentId: string;
  assignmentName: string;
  /** Absent on All Classwork: the grading view resolves its own class and a release spans them all. */
  classroomId?: string;
  classroomName?: string;
  /** FRQ-response counts for the scope above; drive the two-state primary. */
  gradedCount: number;
  totalCount: number;
  /** Graded but not yet released to this class; informs the modal's summary. */
  releasableCount: number;
  /**
   * The assignment has FRQs but nobody has turned one in yet. Both actions are dead, so the split
   * keeps its shape and disables both segments rather than changing size on the row.
   */
  hasSubmissions?: boolean;
  /**
   * Every class in scope has had its grades released, so there is nothing left to release. Optional
   * and defaulted so the two unreleased states are unaffected.
   */
  isReleased?: boolean;
  /** A class in scope withholds results until a due date still ahead; the modal says so (#21337). */
  resultsHiddenUntilDueDate?: boolean;
  /** Set when masquerading, so the scope query reads the same educator's index as the list. */
  asEducatorId?: string;
}

/**
 * Three-state grading affordance on an assignment row:
 *
 * - grade-first ("Grade" + "Release Grades" menu) while responses are ungraded;
 * - release-first ("Release Grades" + "Edit Grades" menu) once every response is graded;
 * - released — the release moves into the menu, disabled, and the primary becomes whichever
 *   grading action still applies, so the prominent slot is never a dead control.
 *
 * Release is offered only while something is actually releasable, which is why `releasableCount`
 * gates release-first as well as the menu item: a finished grade that the release sweep has not
 * picked up yet reads as all-graded, and promoting a dead "Release Grades" to the primary slot
 * would contradict the rule above.
 *
 * Before the first submission arrives both actions are impossible, so the split keeps its shape and
 * disables both segments — the row's width should not depend on whether anyone has answered yet.
 *
 * Meant to sit inside the row's action cluster, which already stops row-click propagation. Without a
 * `classroomId` (All Classwork) every action spans every class the teacher teaches, which is why a
 * partly-released assignment is not `isReleased`.
 */
export const AssignmentGradeSplitButton = ({
  assignmentId,
  assignmentName,
  classroomId,
  classroomName,
  gradedCount,
  totalCount,
  releasableCount,
  hasSubmissions = true,
  isReleased = false,
  resultsHiddenUntilDueDate = false,
  asEducatorId
}: AssignmentGradeSplitButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {allClassesCounts, classroomNames, spansMultipleClasses, isScopeLoading, hasScopeError} =
    useAssignmentReleaseScope({
      assignmentId,
      asEducatorId,
      skip: !isModalOpen
    });

  // Same count the modal shows: published alone reads 0 until the release sweep runs, which would
  // leave a fully graded row offering "Grade" while its own modal reports it finished.
  const allGraded = totalCount > 0 && gradedCount + releasableCount >= totalCount;
  const canRelease = releasableCount > 0;
  const isReleaseDisabled = isReleased || !canRelease;
  // Released puts the release in the menu whatever the grading state, so the primary is always
  // something the teacher can act on rather than a disabled control in the most prominent slot.
  const isReleaseFirst = allGraded && !isReleased && canRelease;

  const goToGrading = () =>
    history.pushState(
      null,
      classroomId
        ? `/classes/${classroomId}/assignments/${assignmentId}/student-detail`
        : `/assignments/${assignmentId}/student-detail`
    );
  const openModal = () => setIsModalOpen(true);

  if (!hasSubmissions) {
    return (
      <WithTooltip content={NO_SUBMISSIONS_TO_GRADE} display='flex' placement='bottom'>
        <SplitButton>
          <Button
            size='s'
            color='primary'
            variant='outlined'
            disabled
            data-testid={`grade-${assignmentId}`}
          >
            Grade
            {/* A disabled button takes no focus, so the tooltip is unreachable without a pointer.
                This carries the same sentence into the accessible name instead. */}
            <span className='sr-only'>{` — ${NO_SUBMISSIONS_TO_GRADE}`}</span>
          </Button>
          <Button
            size='s'
            color='primary'
            variant='outlined'
            disabled
            aria-label='More grade actions'
            data-testid={`grade-actions-${assignmentId}`}
          >
            <Icon icon='caret-down' iconStyle='solid' />
          </Button>
        </SplitButton>
      </WithTooltip>
    );
  }

  return (
    <>
      <SplitButton>
        <Button
          size='s'
          color='primary'
          variant='outlined'
          onClick={isReleaseFirst ? openModal : goToGrading}
          data-testid={`grade-${assignmentId}`}
        >
          {isReleaseFirst ? 'Release Grades' : allGraded ? 'Edit Grades' : 'Grade'}
        </Button>
        <Menu closeOnItemClick position='bottom-end' positionFixed>
          <Menu.Trigger>
            {(triggerProps) => (
              <Button
                {...triggerProps}
                size='s'
                color='primary'
                variant='outlined'
                aria-label='More grade actions'
                data-testid={`grade-actions-${assignmentId}`}
              >
                <Icon icon='caret-down' iconStyle='solid' />
              </Button>
            )}
          </Menu.Trigger>
          {/* Only the inner menu is sized; a narrowed wrapper would let it overflow rightward. */}
          <Menu.Content className='[&>.o-menu-content]:w-[240px] [&>.o-menu-content]:min-w-[240px]'>
            {isReleaseFirst ? (
              <Menu.Item leftIconProps={{icon: 'pen-to-square'}} onClick={goToGrading}>
                Edit Grades
              </Menu.Item>
            ) : (
              // `subLabel` rather than a tooltip: the item is tabIndex={-1} either way and the
              // menu's arrow-key nav skips it, so a hover tooltip would never reach a keyboard or
              // screen-reader user — the ones most in need of the explanation.
              //
              // `readonly` rather than `disabled`, matching the treatment DQA settled on for
              // AssignmentMenuOptions' "View Student Answers": `--disabled` fades the whole row to
              // 0.32 opacity, which would drop this sublabel — already `tertiary` at 13px — to
              // roughly 1.5:1 against the tray. `readonly` blocks the click without the fade, so
              // `aria-disabled`, the tertiary tone and the not-allowed cursor are set explicitly.
              <Menu.Item
                leftIconProps={{
                  icon: 'paper-plane',
                  color: isReleaseDisabled ? 'tertiary' : undefined
                }}
                textProps={isReleaseDisabled ? {color: 'tertiary'} : undefined}
                // `readonly` sets pointer-events: none, which would swallow the cursor too.
                className={
                  isReleaseDisabled ? 'pointer-events-auto! cursor-not-allowed!' : undefined
                }
                aria-disabled={isReleaseDisabled || undefined}
                disableHover={isReleaseDisabled}
                readonly={isReleaseDisabled}
                onClick={isReleaseDisabled ? undefined : openModal}
                subLabel={
                  isReleased
                    ? 'Grades are already released'
                    : canRelease
                      ? undefined
                      : NO_RELEASABLE_SUBMISSIONS
                }
              >
                Release Grades
              </Menu.Item>
            )}
          </Menu.Content>
        </Menu>
      </SplitButton>
      {isModalOpen ? (
        <ReleaseGradesModal
          assignmentId={assignmentId}
          assignmentName={assignmentName}
          classroomId={classroomId}
          classroomName={classroomName}
          gradedCount={gradedCount}
          totalCount={totalCount}
          releasableCount={releasableCount}
          spansMultipleClasses={spansMultipleClasses}
          classroomNames={classroomNames}
          allClassesCounts={allClassesCounts}
          isScopeLoading={isScopeLoading}
          hasScopeError={hasScopeError}
          resultsHiddenUntilDueDate={resultsHiddenUntilDueDate}
          onClose={() => setIsModalOpen(false)}
        />
      ) : null}
    </>
  );
};

export default AssignmentGradeSplitButton;
